import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { MessageSquare, Palette, Settings, Upload, Monitor } from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';
import { authFetch } from '@/utils/auth-fetch';
import { Alert, AlertDescription } from '@/components/ui/alert';

interface ChatbotSettings {
  enabled: boolean;
  tone: 'professional' | 'friendly' | 'casual' | 'technical' | 'fun';
  position: 'bottom-right' | 'bottom-left' | 'top-right' | 'top-left';
  theme: {
    primaryColor: string;
    secondaryColor: string;
    backgroundColor: string;
    textColor: string;
    borderRadius: 'none' | 'small' | 'medium' | 'large';
  };
  appearance: {
    logoUrl?: string;
    customTitle?: string;
    welcomeMessage?: string;
    placeholderText?: string;
    showOnMobile: boolean;
    autoOpen: boolean;
    autoOpenDelay?: number;
  };
  displayMode: 'bubble' | 'sidebar' | 'fullscreen' | 'embedded';
  bubbleIcon: 'default' | 'custom' | 'avatar';
}

interface ChatbotSettingsProps {
  siteId: string;
  isAdmin?: boolean;
}

export function ChatbotSettings({ siteId, isAdmin = false }: ChatbotSettingsProps) {
  const [settings, setSettings] = useState<ChatbotSettings>({
    enabled: true,
    tone: 'friendly',
    position: 'bottom-right',
    theme: {
      primaryColor: '#007bff',
      secondaryColor: '#6c757d',
      backgroundColor: '#ffffff',
      textColor: '#333333',
      borderRadius: 'medium',
    },
    appearance: {
      customTitle: 'Chat with PageMind',
      welcomeMessage: 'Hi! How can I help you today?',
      placeholderText: 'Type your message...',
      showOnMobile: true,
      autoOpen: false,
      autoOpenDelay: 5000,
    },
    displayMode: 'bubble',
    bubbleIcon: 'default',
  });
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    loadSettings();
  }, [siteId]);

  const loadSettings = async () => {
    try {
      const response = await authFetch(`/sites/${siteId}/settings`);
      if (response.ok) {
        const data = await response.json();
        const chatbotSettings = data.settings?.chatbot;
        if (chatbotSettings) {
          setSettings(prev => ({ ...prev, ...chatbotSettings }));
        }
      }
    } catch (error) {
      console.error('Error loading chatbot settings:', error);
      toast({
        title: 'Error',
        description: 'Failed to load chatbot settings',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      const response = await authFetch(`/sites/${siteId}/settings`, {
        method: 'PUT',
        body: JSON.stringify({ 
          settings: { 
            chatbot: settings,
            chatbotEnabled: settings.enabled 
          } 
        }),
      });

      if (response.ok) {
        toast({
          title: 'Success',
          description: 'Chatbot settings updated successfully',
        });
      } else {
        throw new Error('Failed to save settings');
      }
    } catch (error) {
      console.error('Error saving chatbot settings:', error);
      toast({
        title: 'Error',
        description: 'Failed to save chatbot settings',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleThemeChange = (key: keyof typeof settings.theme, value: string) => {
    setSettings(prev => ({
      ...prev,
      theme: { ...prev.theme, [key]: value }
    }));
  };

  const handleAppearanceChange = (key: keyof typeof settings.appearance, value: any) => {
    setSettings(prev => ({
      ...prev,
      appearance: { ...prev.appearance, [key]: value }
    }));
  };

  if (loading) {
    return <div>Loading chatbot settings...</div>;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <MessageSquare className="h-5 w-5" />
          Chatbot Configuration
        </CardTitle>
        <CardDescription>
          Customize the appearance and behavior of your chatbot
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Tone Settings */}
        <div className="space-y-3">
          <Label>Conversation Tone</Label>
          <Select value={settings.tone} onValueChange={(value) => setSettings(prev => ({ ...prev, tone: value as any }))}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="professional">Professional</SelectItem>
              <SelectItem value="friendly">Friendly</SelectItem>
              <SelectItem value="casual">Casual</SelectItem>
              <SelectItem value="technical">Technical</SelectItem>
              <SelectItem value="fun">Fun & Engaging</SelectItem>
            </SelectContent>
          </Select>
          <p className="text-sm text-muted-foreground">
            Sets the personality and language style of your chatbot responses
          </p>
        </div>

        {/* Display Mode */}
        <div className="space-y-3">
          <Label>Display Mode</Label>
          <RadioGroup value={settings.displayMode} onValueChange={(value) => setSettings(prev => ({ ...prev, displayMode: value as any }))}>
            <div className="grid grid-cols-2 gap-4">
              <div className="flex items-start space-x-2">
                <RadioGroupItem value="bubble" id="bubble" />
                <Label htmlFor="bubble" className="cursor-pointer">
                  <div className="font-medium">Bubble</div>
                  <div className="text-xs text-muted-foreground">Floating chat bubble</div>
                </Label>
              </div>
              <div className="flex items-start space-x-2">
                <RadioGroupItem value="sidebar" id="sidebar" />
                <Label htmlFor="sidebar" className="cursor-pointer">
                  <div className="font-medium">Sidebar</div>
                  <div className="text-xs text-muted-foreground">Slide-out panel</div>
                </Label>
              </div>
              <div className="flex items-start space-x-2">
                <RadioGroupItem value="fullscreen" id="fullscreen" />
                <Label htmlFor="fullscreen" className="cursor-pointer">
                  <div className="font-medium">Fullscreen</div>
                  <div className="text-xs text-muted-foreground">Full window chat</div>
                </Label>
              </div>
              <div className="flex items-start space-x-2">
                <RadioGroupItem value="embedded" id="embedded" />
                <Label htmlFor="embedded" className="cursor-pointer">
                  <div className="font-medium">Embedded</div>
                  <div className="text-xs text-muted-foreground">Inline in page</div>
                </Label>
              </div>
            </div>
          </RadioGroup>
        </div>

        {/* Position (only for bubble/sidebar modes) */}
        {(settings.displayMode === 'bubble' || settings.displayMode === 'sidebar') && (
          <div className="space-y-3">
            <Label>Screen Position</Label>
            <Select value={settings.position} onValueChange={(value) => setSettings(prev => ({ ...prev, position: value as any }))}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="bottom-right">Bottom Right</SelectItem>
                <SelectItem value="bottom-left">Bottom Left</SelectItem>
                <SelectItem value="top-right">Top Right</SelectItem>
                <SelectItem value="top-left">Top Left</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}

        {/* Theme Customization */}
        <div className="space-y-4 border-t pt-4">
          <div className="flex items-center gap-2">
            <Palette className="h-4 w-4" />
            <Label>Theme Customization</Label>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="primaryColor">Primary Color</Label>
              <div className="flex gap-2">
                <Input
                  id="primaryColor"
                  type="color"
                  value={settings.theme.primaryColor}
                  onChange={(e) => handleThemeChange('primaryColor', e.target.value)}
                  className="w-20 h-10"
                />
                <Input
                  value={settings.theme.primaryColor}
                  onChange={(e) => handleThemeChange('primaryColor', e.target.value)}
                  placeholder="#007bff"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="backgroundColor">Background Color</Label>
              <div className="flex gap-2">
                <Input
                  id="backgroundColor"
                  type="color"
                  value={settings.theme.backgroundColor}
                  onChange={(e) => handleThemeChange('backgroundColor', e.target.value)}
                  className="w-20 h-10"
                />
                <Input
                  value={settings.theme.backgroundColor}
                  onChange={(e) => handleThemeChange('backgroundColor', e.target.value)}
                  placeholder="#ffffff"
                />
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <Label>Border Radius</Label>
            <RadioGroup value={settings.theme.borderRadius} onValueChange={(value) => handleThemeChange('borderRadius', value)}>
              <div className="flex gap-4">
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="none" id="radius-none" />
                  <Label htmlFor="radius-none">None</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="small" id="radius-small" />
                  <Label htmlFor="radius-small">Small</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="medium" id="radius-medium" />
                  <Label htmlFor="radius-medium">Medium</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="large" id="radius-large" />
                  <Label htmlFor="radius-large">Large</Label>
                </div>
              </div>
            </RadioGroup>
          </div>
        </div>

        {/* Appearance Settings */}
        <div className="space-y-4 border-t pt-4">
          <div className="flex items-center gap-2">
            <Settings className="h-4 w-4" />
            <Label>Appearance Settings</Label>
          </div>
          
          <div className="space-y-3">
            <div>
              <Label htmlFor="customTitle">Chat Window Title</Label>
              <Input
                id="customTitle"
                value={settings.appearance.customTitle || ''}
                onChange={(e) => handleAppearanceChange('customTitle', e.target.value)}
                placeholder="Chat with PageMind"
              />
            </div>
            
            <div>
              <Label htmlFor="welcomeMessage">Welcome Message</Label>
              <Textarea
                id="welcomeMessage"
                value={settings.appearance.welcomeMessage || ''}
                onChange={(e) => handleAppearanceChange('welcomeMessage', e.target.value)}
                placeholder="Hi! How can I help you today?"
                rows={2}
              />
            </div>
            
            <div>
              <Label htmlFor="placeholderText">Input Placeholder</Label>
              <Input
                id="placeholderText"
                value={settings.appearance.placeholderText || ''}
                onChange={(e) => handleAppearanceChange('placeholderText', e.target.value)}
                placeholder="Type your message..."
              />
            </div>
          </div>

          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label htmlFor="showOnMobile">Show on Mobile Devices</Label>
              <input
                type="checkbox"
                id="showOnMobile"
                checked={settings.appearance.showOnMobile}
                onChange={(e) => handleAppearanceChange('showOnMobile', e.target.checked)}
                className="h-4 w-4"
              />
            </div>
            
            <div className="flex items-center justify-between">
              <div>
                <Label htmlFor="autoOpen">Auto-open Chat</Label>
                <p className="text-xs text-muted-foreground">Automatically open chat after delay</p>
              </div>
              <input
                type="checkbox"
                id="autoOpen"
                checked={settings.appearance.autoOpen}
                onChange={(e) => handleAppearanceChange('autoOpen', e.target.checked)}
                className="h-4 w-4"
              />
            </div>
            
            {settings.appearance.autoOpen && (
              <div>
                <Label htmlFor="autoOpenDelay">Auto-open Delay (ms)</Label>
                <Input
                  id="autoOpenDelay"
                  type="number"
                  value={settings.appearance.autoOpenDelay || 5000}
                  onChange={(e) => handleAppearanceChange('autoOpenDelay', parseInt(e.target.value))}
                  min="1000"
                  step="1000"
                />
              </div>
            )}
          </div>
        </div>

        {/* Preview Section */}
        <div className="border-t pt-4">
          <Alert>
            <Monitor className="h-4 w-4" />
            <AlertDescription>
              <strong>Preview:</strong> Changes will be reflected on your website after saving. 
              You can preview the chatbot by visiting your site with the PageMind script installed.
            </AlertDescription>
          </Alert>
        </div>

        {/* Save Button */}
        <div className="flex justify-end">
          <Button onClick={saveSettings} disabled={saving}>
            {saving ? 'Saving...' : 'Save Chatbot Settings'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}